import {
  Badge,
  CheckCircle,
  DeleteOutline,
  Groups,
  PlayCircleOutline,
  Schedule,
  StopCircle,
} from "@mui/icons-material";
import { Box, Chip, IconButton, Tooltip, Typography } from "@mui/material";
import { ReactNode, useCallback } from "react";
import { useLocaleState, useTranslate } from "react-admin";

import { getActivityContentTitle, getCmsContentKind } from "../lib/activityUtils";
import { formatDateTimeInline } from "../lib/dateUtils";
import { formatName } from "../lib/formatName";
import {
  ActivitySessionStatus,
  borderRadius,
  color,
  sessionStatusStyle,
  shadows,
  typography,
} from "../theme/designTokens";
import { ProcedureRecord } from "../types/procedure";
import { ActivityRowHeader } from "./ActivityRowHeader";
import { ActivityThumbnail } from "./ActivityThumbnail";
import { ContentKindIcon } from "./ContentKindIcon";

type ProcedureActivitySession = NonNullable<ProcedureRecord["activitySessions"]>["nodes"][number];

const getStatus = (session: ProcedureActivitySession): ActivitySessionStatus => {
  if (session.endedAt) return "done";
  if (session.beganAt) return "running";
  return "pending";
};

const STATUS_ICON: Record<ActivitySessionStatus, ReactNode> = {
  pending: <Schedule sx={{ fontSize: "16px !important" }} />,
  running: <PlayCircleOutline sx={{ fontSize: "16px !important" }} />,
  done: <CheckCircle sx={{ fontSize: "16px !important" }} />,
};

const SessionStatusChip = ({ status }: { status: ActivitySessionStatus }) => {
  const translate = useTranslate();
  const style = sessionStatusStyle[status];

  return (
    <Chip
      icon={STATUS_ICON[status] as React.ReactElement}
      label={translate(`Procedure.session.status.${status}`)}
      sx={{
        backgroundColor: style.bg,
        color: `${style.color} !important`,
        border: `1px solid ${style.border}`,
        fontWeight: typography.fontWeight.semibold,
        "& .MuiChip-icon": { color: `${style.color} !important` },
      }}
    />
  );
};

const SessionField = ({
  icon,
  label,
  value,
  placeholder = "–",
}: {
  icon: ReactNode;
  label: string;
  value: string | null;
  placeholder?: string;
}) => {
  const isEmpty = !value;
  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: "11px", padding: "14px 20px" }}>
      <Box sx={{ color: color.iconMuted, mt: "1px", display: "flex" }}>{icon}</Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: typography.fontSize.xxs, color: "text.secondary", mb: "3px" }}>{label}</Typography>
        <Typography
          sx={{
            fontSize: typography.fontSize.xs,
            fontWeight: isEmpty ? typography.fontWeight.normal : typography.fontWeight.medium,
            color: isEmpty ? color.iconMuted : "text.primary",
            lineHeight: typography.lineHeight.sm,
          }}
        >
          {value || placeholder}
        </Typography>
      </Box>
    </Box>
  );
};

export const ProcedureActivitySessionCard = ({
  procedureId,
  session,
  index,
  canRemove,
  onRemove,
}: {
  procedureId: string;
  session: ProcedureActivitySession;
  index: number;
  canRemove: boolean;
  onRemove: (session: { procedureId: string; sessionId: string; title: string }) => void;
}) => {
  const translate = useTranslate();
  const [locale] = useLocaleState();

  const { contentTitle, contentTitleKey } = getActivityContentTitle(session);
  const title =
    contentTitle ||
    (contentTitleKey ? translate(contentTitleKey) : "") ||
    translate("Procedure.fields.unknownActivity");

  const cmsContentKind = getCmsContentKind(session);
  const thumbnailFilename = session.activity?.contentActivity?.content?.thumbnailFilename ?? null;
  const status = getStatus(session);

  const caregiverName = formatName(session.caregiver) || null;
  const participantNames =
    session.activityParticipants?.nodes
      ?.map(participant => participant.senior?.name)
      .filter((name): name is string => Boolean(name))
      .join(", ") || null;

  const cellBorder = `1px solid ${color.datagridBorder}`;
  const handleRemoveClick = useCallback(() => {
    onRemove({ procedureId, sessionId: session.id, title });
  }, [onRemove, procedureId, session.id, title]);

  return (
    <Box
      sx={{
        border: `1px solid ${color.panelBorder}`,
        borderRadius: borderRadius.sm,
        backgroundColor: color.white,
        boxShadow: shadows.card,
        overflow: "hidden",
        mb: "14px",
      }}
      data-testid="procedure-session-card"
    >
      <Box sx={{ px: 2.5, py: 2 }}>
        <ActivityRowHeader
          leading={<ActivityThumbnail thumbnailFilename={thumbnailFilename} cmsContentKind={cmsContentKind} />}
          eyebrow={translate("Procedure.session.order", { number: index + 1 })}
          title={title}
          titleIcon={cmsContentKind ? <ContentKindIcon contentKind={cmsContentKind} size={20} /> : undefined}
          trailing={
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <SessionStatusChip status={status} />
              {canRemove && (
                <Tooltip title={translate("Procedure.session.removeAction")}>
                  <IconButton
                    aria-label={translate("Procedure.session.removeAction")}
                    color="error"
                    size="small"
                    onClick={handleRemoveClick}
                    data-testid="procedure-session-remove"
                  >
                    <DeleteOutline fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          }
        />
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "repeat(2, 1fr)", md: "repeat(4, 1fr)" },
          borderTop: cellBorder,
          backgroundColor: color.background2,
          "& > *:not(:first-of-type)": { borderLeft: { md: cellBorder } },
        }}
      >
        <SessionField
          icon={<PlayCircleOutline sx={{ fontSize: 19 }} />}
          label={translate("Procedure.fields.sessionStart")}
          value={session.beganAt ? formatDateTimeInline(session.beganAt, locale) : null}
          placeholder={translate("Procedure.session.notStarted")}
        />
        <SessionField
          icon={<StopCircle sx={{ fontSize: 19 }} />}
          label={translate("Procedure.fields.sessionEnd")}
          value={session.endedAt ? formatDateTimeInline(session.endedAt, locale) : null}
        />
        <SessionField
          icon={<Badge sx={{ fontSize: 19 }} />}
          label={translate("Procedure.fields.caregiver")}
          value={caregiverName}
          placeholder={translate("Procedure.session.notAssigned")}
        />
        <SessionField
          icon={<Groups sx={{ fontSize: 19 }} />}
          label={translate("Procedure.fields.participants")}
          value={participantNames}
        />
      </Box>
    </Box>
  );
};
